// src/config/adminApi.js
import axios from "axios";
import { store } from "../redux/store";
import { logoutAdmin } from "../redux/slices/adminSlice"; // Import the specific admin logout action

const ADMIN_API_BASE_URL =
  process.env.NEXT_PUBLIC_ADMIN_API_URL || "https://hyreback.hyrelancer.com/api/admin";

const adminApi = axios.create({
  baseURL: ADMIN_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor to attach the admin token
adminApi.interceptors.request.use(
  (config) => {
    try {
      // ensure headers object exists
      config.headers = config.headers || {};

      let adminToken = null;
      if (typeof window !== "undefined") {
        adminToken = localStorage.getItem("adminToken") || store.getState()?.admin?.token;
      } else {
        // server-side: read token from redux store if available
        adminToken = store.getState()?.admin?.token;
      }

      if (adminToken) {
        config.headers.Authorization = `Bearer ${adminToken}`;
      }
    } catch (e) {
      console.warn("Error attaching admin token:", e);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle 401 Unauthorized for admin
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      if (error?.response?.status === 401) {
        // best-effort dispatch; fail quietly if logoutAdmin isn't available
        try {
          store.dispatch(logoutAdmin());
        } catch (e) {
          console.warn("logoutAdmin dispatch failed:", e);
        }

        if (typeof window !== "undefined") {
          localStorage.removeItem("adminToken");
          window.location.href = "/gateway"; // Redirect to admin login page (change if needed)
        }
      }
    } catch (e) {
      console.warn("adminApi response interceptor error:", e);
    }
    return Promise.reject(error);
  }
);

/**
 * Helpers to manage admin token programmatically
 */
export function setAdminToken(token) {
  if (token) {
    adminApi.defaults.headers = adminApi.defaults.headers || {};
    adminApi.defaults.headers.Authorization = `Bearer ${token}`;
    try {
      if (typeof window !== "undefined") localStorage.setItem("adminToken", token);
    } catch (e) {
      console.warn("setAdminToken localStorage write failed:", e);
    }
  } else {
    delete adminApi.defaults.headers.Authorization;
    try {
      if (typeof window !== "undefined") localStorage.removeItem("adminToken");
    } catch (e) {
      console.warn("clear adminToken localStorage failed:", e);
    }
  }
}

export function clearAdminToken() {
  setAdminToken(null);
}

/**
 * Admin Dashboard API calls
 */
export const adminDashboardApi = {
  // Get dashboard analytics data
  getDashboardData: async () => {
    try {
      const response = await adminApi.get('/AdminDashboard');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }
};

/**
 * Admin Messages API calls
 */
export const adminMessagesApi = {
  // Get all messages
  getMessages: async () => {
    try {
      const response = await adminApi.get('/messages');
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  // Get single message
  getMessage: async (encodedId) => {
    try {
      const response = await adminApi.get(`/messages/${encodedId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching message:', error);
      throw error;
    }
  },

  // Create new message
  createMessage: async (messageData) => {
    try {
      const response = await adminApi.post('/messages', messageData);
      return response.data;
    } catch (error) {
      console.error('Error creating message:', error);
      throw error;
    }
  },

  // Update message
  updateMessage: async (encodedId, messageData) => {
    try {
      const response = await adminApi.put(`/messages/${encodedId}`, messageData);
      return response.data;
    } catch (error) {
      console.error('Error updating message:', error);
      throw error;
    }
  },

  // Toggle message status (delete/restore)
  toggleMessageStatus: async (encodedId) => {
    try {
      const response = await adminApi.delete(`/messages/${encodedId}`);
      return response.data;
    } catch (error) {
      console.error('Error toggling message status:', error);
      throw error;
    }
  },

  // Send mail to users
  sendMail: async (payload) => {
    try {
      const formData = new FormData();
      if (payload.subject) formData.append('subject', payload.subject);
      if (payload.description) formData.append('description', payload.description);

      // Handle emails array
      if (payload.emails && Array.isArray(payload.emails)) {
        payload.emails.forEach((email, index) => {
          formData.append(`emails[${index}]`, email);
        });
      }

      // Handle images array
      if (payload.images && Array.isArray(payload.images)) {
        payload.images.forEach((file, index) => {
          formData.append(`images[${index}]`, file);
        });
      }

      // Use the correct endpoint provided by user
      const response = await adminApi.post('/send-bulk-mail', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Error sending mail:', error);
      throw error;
    }
  }
};

/**
 * Admin Notifications API calls
 */
export const adminNotificationsApi = {
  // Get all notifications
  getNotifications: async () => {
    try {
      const response = await adminApi.get('/AdminNotifications');
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    try {
      const response = await adminApi.post(`/AdminNotifications/read/${notificationId}`);
      return response.data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    try {
      const response = await adminApi.post('/AdminNotifications/read_all');
      return response.data;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  },

  // Mark notifications by type as read
  markByTypeAsRead: async (type) => {
    try {
      const response = await adminApi.post(`/AdminNotifications/read_by_type/${type}`);
      return response.data;
    } catch (error) {
      console.error('Error marking notifications by type as read:', error);
      throw error;
    }
  }
};

/**
 * Admin CMS Pages API calls
 */
export const adminCmsApi = {
  // Store a new CMS content section
  cmsContentStore: async (payload) => {
    try {
      const response = await adminApi.post('/cmscontentstore', payload);
      return response.data;
    } catch (error) {
      console.error('Error storing CMS content:', error);
      throw error;
    }
  },

  // Update CMS page title and description
  updateCmsPage: async (encodedCmsId, payload) => {
    try {
      const response = await adminApi.put(`/cms-page/update/${encodedCmsId}`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating CMS page:', error);
      throw error;
    }
  },

  // Bulk update all content sections for a CMS page
  updateAllContents: async (payload) => {
    try {
      const response = await adminApi.put('/cms-content/update-all', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating all CMS contents:', error);
      throw error;
    }
  },

  // Delete (deactivate) a content section by base64 id
  deleteContent: async (encodedId) => {
    try {
      const response = await adminApi.delete(`/cmscontentdelete/${encodedId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting CMS content:', error);
      throw error;
    }
  },

  // Update positions (numbers) for content sections
  updatePositions: async (payload) => {
    try {
      const response = await adminApi.put('/cms-content/position', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating CMS content positions:', error);
      throw error;
    }
  },
};

/**
 * Admin Badges API calls
 */
export const adminBadgesApi = {
  // List badges
  list: async () => {
    try {
      const response = await adminApi.get('/badges');
      return response.data;
    } catch (error) {
      console.error('Error fetching badges:', error);
      throw error;
    }
  },

  // Create badge (multipart form)
  create: async (payload) => {
    try {
      const formData = new FormData();
      if (payload.badge_name) formData.append('badge_name', payload.badge_name);
      if (payload.badge_description) formData.append('badge_description', payload.badge_description);
      if (payload.badge_rank !== undefined && payload.badge_rank !== null) formData.append('badge_rank', payload.badge_rank);
      if (payload.badge_percentage !== undefined && payload.badge_percentage !== null) formData.append('badge_percentage', payload.badge_percentage);
      if (payload.badge_is_active !== undefined && payload.badge_is_active !== null) formData.append('badge_is_active', payload.badge_is_active);
      if (payload.badge_icon) formData.append('badge_icon', payload.badge_icon);

      const response = await adminApi.post('/badges', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating badge:', error);
      throw error;
    }
  },

  // Update badge (POST with multipart form). id must be base64 encoded
  update: async (base64Id, payload) => {
    try {
      const formData = new FormData();
      if (payload.badge_name !== undefined) formData.append('badge_name', payload.badge_name);
      if (payload.badge_description !== undefined) formData.append('badge_description', payload.badge_description);
      if (payload.badge_rank !== undefined) formData.append('badge_rank', payload.badge_rank);
      if (payload.badge_percentage !== undefined) formData.append('badge_percentage', payload.badge_percentage);
      if (payload.badge_is_active !== undefined) formData.append('badge_is_active', payload.badge_is_active);
      if (payload.badge_icon) formData.append('badge_icon', payload.badge_icon);

      const response = await adminApi.post(`/badges/${base64Id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating badge:', error);
      throw error;
    }
  },

  // Delete (soft delete) badge by base64 id
  remove: async (base64Id) => {
    try {
      const response = await adminApi.delete(`/badges/${base64Id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting badge:', error);
      throw error;
    }
  }
};

/**
 * Admin SEO Pages API calls
 */
export const adminSeoApi = {
  // Get list of SEO pages
  getSeoPages: async () => {
    try {
      const response = await adminApi.get('/seo_page_list');
      return response.data;
    } catch (error) {
      console.error('Error fetching SEO pages:', error);
      throw error;
    }
  },

  // Get list of SEO types
  getSeoTypes: async () => {
    try {
      const response = await adminApi.get('/seo_type_list');
      return response.data;
    } catch (error) {
      console.error('Error fetching SEO types:', error);
      throw error;
    }
  },

  // Get detailed SEO page data by base64 encoded cms_id
  getSeoPageData: async (encodedCmsId) => {
    try {
      const response = await adminApi.get(`/seo_page_data/${encodedCmsId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching SEO page data:', error);
      throw error;
    }
  },

  // Store/update SEO page data
  storeSeoPages: async (payload) => {
    try {
      const response = await adminApi.post('/store_seo_pages', payload);
      return response.data;
    } catch (error) {
      console.error('Error storing SEO pages:', error);
      throw error;
    }
  }
};

/**
 * Admin Roles API calls
 */
export const adminRolesApi = {
  // Get list of roles
  getRoles: async () => {
    try {
      const response = await adminApi.get('/RoleList');
      return response.data;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  },

  // Store/create a new role
  storeRole: async (payload) => {
    try {
      const response = await adminApi.post('/storeRole', payload);
      return response.data;
    } catch (error) {
      console.error('Error storing role:', error);
      throw error;
    }
  },

  // Get permissions for a role
  listPermissions: async (roleId) => {
    try {
      const response = await adminApi.get(`/listPermissions/${roleId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching permissions:', error);
      throw error;
    }
  },

  // Save all permissions for a role
  saveAllPermissions: async (payload) => {
    try {
      const response = await adminApi.post('/saveAllPermissions', payload);
      return response.data;
    } catch (error) {
      console.error('Error saving permissions:', error);
      throw error;
    }
  }
};

/**
 * Admin Menus API calls
 */
export const adminMenusApi = {
  // Get list of menus
  getMenus: async () => {
    try {
      const response = await adminApi.get('/menuList');
      return response.data;
    } catch (error) {
      console.error('Error fetching menus:', error);
      throw error;
    }
  },

  // Store/create a new menu
  storeMenu: async (payload) => {
    try {
      const response = await adminApi.post('/storeMenu', payload);
      return response.data;
    } catch (error) {
      console.error('Error storing menu:', error);
      throw error;
    }
  },

  // Delete a menu
  destroyMenu: async (menuId) => {
    try {
      const response = await adminApi.delete(`/destroyMenu/${menuId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting menu:', error);
      throw error;
    }
  }
};

/**
 * Admin Users API calls
 */
export const adminUsersApi = {
  // Get list of admin users
  getAdminUsers: async () => {
    try {
      const response = await adminApi.get('/adUserList');
      return response.data;
    } catch (error) {
      console.error('Error fetching admin users:', error);
      throw error;
    }
  },

  // Store/create a new admin user
  storeAdminUser: async (payload) => {
    try {
      const response = await adminApi.post('/storeAdUser', payload);
      return response.data;
    } catch (error) {
      console.error('Error storing admin user:', error);
      throw error;
    }
  },

  // Update an admin user
  updateAdminUser: async (payload) => {
    try {
      const response = await adminApi.post('/updateAdUser', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating admin user:', error);
      throw error;
    }
  }
};

export default adminApi;
